import { Account, Transaction, Subscription, Bill } from '../types';
import { addMonths, subMonths, format, eachDayOfInterval, startOfMonth, endOfMonth } from 'date-fns';

// Helper function to generate random amount within a range
const randomAmount = (min: number, max: number): number => {
  return Number((Math.random() * (max - min) + min).toFixed(2));
};

// Helper function to pick random item from array
const randomItem = <T>(items: T[]): T => {
  return items[Math.floor(Math.random() * items.length)];
};

// Categories with typical amount ranges
const expenseCategories = [
  { name: 'Housing', min: 800, max: 2000 },
  { name: 'Food', min: 200, max: 600 },
  { name: 'Transportation', min: 100, max: 400 },
  { name: 'Utilities', min: 100, max: 300 },
  { name: 'Healthcare', min: 50, max: 500 },
  { name: 'Entertainment', min: 50, max: 300 },
  { name: 'Shopping', min: 50, max: 500 },
  { name: 'Education', min: 100, max: 1000 },
  { name: 'Personal Care', min: 30, max: 200 },
  { name: 'Insurance', min: 100, max: 400 }
];

const incomeCategories = [
  { name: 'Salary', min: 3000, max: 8000 },
  { name: 'Freelance', min: 500, max: 3000 },
  { name: 'Investments', min: 100, max: 1000 },
  { name: 'Rental Income', min: 800, max: 2500 },
  { name: 'Side Business', min: 300, max: 2000 }
];

// Common transaction descriptions
const expenseDescriptions: Record<string, string[]> = {
  Housing: ['Rent Payment', 'Mortgage Payment', 'Property Tax', 'Home Insurance', 'Home Repairs', 'Home Maintenance'],
  Food: ['Grocery Shopping', 'Restaurant Dinner', 'Coffee Shop', 'Food Delivery', 'Lunch', 'Snacks'],
  Transportation: ['Gas Station', 'Car Insurance', 'Car Maintenance', 'Public Transit', 'Uber Ride', 'Parking Fee'],
  Utilities: ['Electricity Bill', 'Water Bill', 'Gas Bill', 'Internet Bill', 'Phone Bill', 'Trash Service'],
  Healthcare: ['Doctor Visit', 'Prescription', 'Dental Care', 'Health Insurance', 'Eye Care', 'Medical Test'],
  Entertainment: ['Movie Tickets', 'Concert Tickets', 'Streaming Service', 'Gaming', 'Sports Event', 'Hobby Supplies'],
  Shopping: ['Clothing Purchase', 'Electronics', 'Home Goods', 'Online Shopping', 'Gift Purchase', 'Books'],
  Education: ['Tuition Payment', 'School Supplies', 'Online Course', 'Textbooks', 'Training Program', 'Workshop Fee'],
  'Personal Care': ['Haircut', 'Gym Membership', 'Spa Service', 'Personal Trainer', 'Beauty Products', 'Wellness Products'],
  Insurance: ['Life Insurance', 'Car Insurance', 'Home Insurance', 'Health Insurance', 'Disability Insurance']
};

const incomeDescriptions: Record<string, string[]> = {
  Salary: ['Monthly Salary', 'Bi-weekly Paycheck', 'Bonus Payment', 'Commission', 'Overtime Pay'],
  Freelance: ['Client Payment', 'Project Fee', 'Consulting Work', 'Contract Work', 'Freelance Project'],
  Investments: ['Stock Dividend', 'Interest Income', 'Investment Return', 'Capital Gains', 'Crypto Gains'],
  'Rental Income': ['Apartment Rent', 'Property Rental', 'Airbnb Income', 'Parking Space Rental'],
  'Side Business': ['Online Sales', 'Service Income', 'Product Sales', 'Commission Income']
};

// Generate transactions for a given date range
const generateTransactions = (startDate: Date, endDate: Date, userId: string, accountId: string): Transaction[] => {
  const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
  const transactions: Transaction[] = [];
  let id = 1;

  dateRange.forEach(date => {
    // Generate 2-5 transactions per day
    const numTransactions = Math.floor(Math.random() * 4) + 2;

    for (let i = 0; i < numTransactions; i++) {
      const isIncome = Math.random() < 0.2; // 20% chance of being income
      const category = randomItem(isIncome ? incomeCategories : expenseCategories);
      const descriptions = isIncome ? incomeDescriptions[category.name] : expenseDescriptions[category.name];
      
      transactions.push({
        id: String(id++),
        user_id: userId,
        account_id: accountId,
        amount: randomAmount(category.min, category.max),
        description: randomItem(descriptions),
        category: category.name,
        date: format(date, 'yyyy-MM-dd'),
        type: isIncome ? 'income' : 'expense'
      });
    }

    // Always add salary on 1st of the month
    if (date.getDate() === 1) {
      transactions.push({
        id: String(id++),
        user_id: userId,
        account_id: accountId,
        amount: randomAmount(4000, 5000),
        description: 'Monthly Salary',
        category: 'Salary',
        date: format(date, 'yyyy-MM-dd'),
        type: 'income'
      });
    }
  });

  return transactions;
};

// Generate recurring bills
const generateBills = (userId: string, accountId: string): Bill[] => {
  const startDate = subMonths(new Date(), 6);
  const bills: Bill[] = [];
  let id = 1;

  // Generate 12 months of bills
  for (let i = 0; i < 12; i++) {
    const date = addMonths(startDate, i);
    
    // Rent/Mortgage
    bills.push({
      id: String(id++),
      user_id: userId,
      account_id: accountId,
      name: 'Rent Payment',
      amount: 1500,
      dueDate: format(date, 'yyyy-MM-15'),
      category: 'Housing',
      status: date > new Date() ? 'pending' : 'paid',
      description: 'Monthly rent payment',
      isRecurring: true,
      frequency: 'Monthly',
      startDate: format(subMonths(date, 6), 'yyyy-MM-dd'),
      lastModified: format(date, 'yyyy-MM-dd')
    });

    // Utilities
    ['Electricity Bill', 'Water Bill', 'Internet Service'].forEach((name, index) => {
      bills.push({
        id: String(id++),
        user_id: userId,
        account_id: accountId,
        name,
        amount: randomAmount(80, 200),
        dueDate: format(date, `yyyy-MM-${10 + index}`),
        category: 'Utilities',
        status: date > new Date() ? 'pending' : 'paid',
        description: `Monthly ${name.toLowerCase()}`,
        isRecurring: true,
        frequency: 'Monthly',
        startDate: format(subMonths(date, 6), 'yyyy-MM-dd'),
        lastModified: format(date, 'yyyy-MM-dd')
      });
    });
  }

  return bills;
};

// Generate recurring subscriptions
const generateSubscriptions = (userId: string, accountId: string): Subscription[] => {
  const subscriptions: Subscription[] = [
    {
      id: '1',
      user_id: userId,
      account_id: accountId,
      name: 'Netflix',
      amount: 15.99,
      frequency: 'Monthly',
      category: 'Entertainment',
      nextCharge: format(addMonths(new Date(), 1), 'yyyy-MM-15'),
      status: 'active',
      startDate: format(subMonths(new Date(), 6), 'yyyy-MM-15'),
      lastModified: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '2',
      user_id: userId,
      account_id: accountId,
      name: 'Gym Membership',
      amount: 49.99,
      frequency: 'Monthly',
      category: 'Health',
      nextCharge: format(addMonths(new Date(), 1), 'yyyy-MM-01'),
      status: 'active',
      startDate: format(subMonths(new Date(), 3), 'yyyy-MM-01'),
      lastModified: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '3',
      user_id: userId,
      account_id: accountId,
      name: 'Spotify',
      amount: 9.99,
      frequency: 'Monthly',
      category: 'Entertainment',
      nextCharge: format(addMonths(new Date(), 1), 'yyyy-MM-10'),
      status: 'active',
      startDate: format(subMonths(new Date(), 12), 'yyyy-MM-10'),
      lastModified: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '4',
      user_id: userId,
      account_id: accountId,
      name: 'Cloud Storage',
      amount: 19.99,
      frequency: 'Monthly',
      category: 'Technology',
      nextCharge: format(addMonths(new Date(), 1), 'yyyy-MM-05'),
      status: 'active',
      startDate: format(subMonths(new Date(), 8), 'yyyy-MM-05'),
      lastModified: format(new Date(), 'yyyy-MM-dd')
    }
  ];

  return subscriptions;
};

// Mock accounts data
export const mockAccounts: Account[] = [
  {
    id: '1',
    user_id: '1',
    name: 'Main Checking',
    type: 'debit',
    balance: 3500.75,
    currency: 'USD',
    institution: 'Chase Bank',
    account_number: '4321',
    color: '#3B82F6',
    is_default: true,
    created_at: '2025-01-15T00:00:00Z'
  },
  {
    id: '2',
    user_id: '1',
    name: 'Savings Account',
    type: 'savings',
    balance: 12750.42,
    currency: 'USD',
    institution: 'Bank of America',
    account_number: '8765',
    color: '#10B981',
    is_default: false,
    created_at: '2025-01-20T00:00:00Z'
  },
  {
    id: '3',
    user_id: '1',
    name: 'Travel Credit Card',
    type: 'credit',
    balance: -450.25,
    currency: 'USD',
    institution: 'American Express',
    account_number: '9876',
    color: '#6366F1',
    is_default: false,
    created_at: '2025-02-05T00:00:00Z'
  },
  {
    id: '4',
    user_id: '1',
    name: 'Investment Portfolio',
    type: 'investment',
    balance: 28500.00,
    currency: 'USD',
    institution: 'Vanguard',
    account_number: '5432',
    color: '#F59E0B',
    is_default: false,
    created_at: '2025-02-10T00:00:00Z'
  }
];

// Generate 6 months of transaction data
const startDate = subMonths(startOfMonth(new Date()), 6);
const endDate = endOfMonth(new Date());

export const mockTransactions = generateTransactions(startDate, endDate, '1', '1');
export const mockBills = generateBills('1', '1');
export const mockSubscriptions = generateSubscriptions('1', '1');